import { Flex, Text } from '@chakra-ui/react';
import { faFloppyDisk } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Checkbox,
  Input,
  Modal,
  Radio,
  Select,
  useToast
} from '@morressier/alembic';
import { ICOCConfig } from '@publishing-engine/domain/models';
import { useCallback, useEffect, useMemo, useState } from 'react';

import FlexibleMultiSelect, {
  MultiSelectOption
} from 'src/components/FlexibleMultiSelect';

import { Decision, ModalMode, RoleType } from './types';

// ---- Decision options (shared) ----

export const DECISION_OPTIONS: MultiSelectOption[] = [
  { value: 'accept', label: 'Accept' },
  { value: 'reject', label: 'Reject' },
  { value: 'rejectWithoutReview', label: 'Reject without Review' },
  { value: 'revision', label: 'Revision' }
];

// ---- Constants ----

/** Upper bound shared by Role Max Limit and Max Recommendations Required */
const LIMIT_MAX = 10;

// ---- Helpers ----

const decisionsToValues = (decisions: Decision[]): string[] =>
  decisions.map(d => d.value);

const valuesToDecisions = (values: string[]): Decision[] =>
  values.map(v => {
    const option = DECISION_OPTIONS.find(o => o.value === v);
    return option
      ? { value: option.value, label: option.label }
      : { value: v, label: v };
  });

const createEmptyRow = (): ICOCConfig => ({
  role: '',
  roleAbbreviation: '',
  roleLevel: 0,
  autoAssign: false,
  isOwner: false,
  isDecisionMaker: false,
  onlyAssignedSubmissionVisibility: false,
  decision: []
});

// Integer-only fields — decimals are dropped (e.g. "1.9" -> 1)
const toOptionalInteger = (value: string): number | undefined => {
  if (value === '') return undefined;

  const number = Number(value);

  return Number.isNaN(number) ? undefined : Math.trunc(number);
};

// ---- Types ----

export interface RoleFormModalProps {
  isOpen: boolean;
  mode: ModalMode;
  type: RoleType;
  editingRole?: ICOCConfig;
  availableRoles: string[];
  /** Currently assigned role levels among other roles (owner always level 1) */
  assignedRoleLevels: number[];
  /** Role names that should be excluded from the role dropdown */
  excludedRoles?: string[];
  isSaving: boolean;
  onClose: () => void;
  onSave: (role: ICOCConfig) => void;
  roleDisplayNames: Record<string, string> | undefined;
  isCOCRecommendationRequired?: boolean;
  /**
   * True when the journal is active or already has submissions. A role's saved
   * max limit may then be raised but not lowered.
   */
  isJournalLocked?: boolean;
}

// ---- The shared modal ----

const RoleFormModal: React.FC<RoleFormModalProps> = ({
  isOpen,
  mode,
  type,
  editingRole,
  availableRoles,
  assignedRoleLevels,
  excludedRoles = [],
  isSaving,
  onClose,
  onSave,
  roleDisplayNames,
  isCOCRecommendationRequired,
  isJournalLocked
}) => {
  const toast = useToast();

  const isOwner = type === 'owner';

  // Initialize form state from editingRole (edit) or empty (add)
  // Owner roles always have roleLevel 1
  // Other roles: auto-assign next sequential level (2, 3, 4...) in add mode
  const seed = useMemo(() => {
    if (mode === 'edit' && editingRole) {
      return { ...editingRole, roleLevel: isOwner ? 1 : editingRole.roleLevel };
    }
    if (isOwner) {
      return { ...createEmptyRow(), roleLevel: 1 };
    }
    // Add mode for other roles: find the next available level starting from 2
    const sortedLevels = [...assignedRoleLevels].sort((a, b) => a - b);
    let nextLevel = 2;
    for (const lv of sortedLevels) {
      if (lv === nextLevel) {
        nextLevel++;
      } else if (lv > nextLevel) {
        break;
      }
    }
    return { ...createEmptyRow(), roleLevel: nextLevel };
  }, [mode, editingRole, isOwner, assignedRoleLevels]);

  const [form, setForm] = useState<ICOCConfig>(seed);

  // Filter out roles already assigned elsewhere (keep the editing role's own name)
  const selectableRoles = useMemo(
    () =>
      availableRoles.filter(
        r =>
          !excludedRoles.includes(r) ||
          (mode === 'edit' && r === editingRole?.role)
      ),
    [availableRoles, excludedRoles, mode, editingRole?.role]
  );

  // Reset form when modal opens with new seed
  useEffect(() => {
    setForm(seed);
  }, [seed]);

  // Auto-populate abbreviation from roleDisplayNames when role is selected in add mode
  useEffect(() => {
    if (mode === 'add' && form.role && roleDisplayNames?.[form.role]) {
      setForm(prev => ({
        ...prev,
        roleAbbreviation: roleDisplayNames[form.role]
      }));
    }
  }, [form.role, mode, roleDisplayNames]);

  const updateField = useCallback(
    <K extends keyof ICOCConfig>(field: K, value: ICOCConfig[K]) => {
      setForm(prev => ({ ...prev, [field]: value }));
    },
    []
  );

  /**
   * Lower bound for Role Max Limit. Once the journal is live, an already-saved
   * limit may only be raised — lowering it would re-open capacity that existing
   * submissions are accounted against. Undefined means the field is free.
   */
  const roleMaxLimitFloor =
    isJournalLocked &&
    mode === 'edit' &&
    editingRole?.roleMaxLimit !== undefined
      ? editingRole.roleMaxLimit
      : undefined;

  const handleSave = () => {
    // Basic validation
    if (!form.role) {
      toast({
        variant: 'error',
        title: 'Role is required',
        isClosable: true
      });
      return;
    }

    // Role level validation — mandatory for other roles
    if (!isOwner && !form.roleLevel) {
      toast({
        variant: 'error',
        title: 'Role level is required',
        isClosable: true
      });
      return;
    }

    // Role max limit validation — mandatory for every role
    if (form.roleMaxLimit === undefined) {
      toast({
        variant: 'error',
        title: 'Role max limit is required',
        isClosable: true
      });
      return;
    }

    if (form.roleMaxLimit !== undefined && form.roleMaxLimit < 1) {
      toast({
        variant: 'error',
        title: 'Role max limit must be at least 1',
        isClosable: true
      });
      return;
    }

    // Saved limits may only be raised once the journal is live
    if (
      roleMaxLimitFloor !== undefined &&
      form.roleMaxLimit !== undefined &&
      form.roleMaxLimit < roleMaxLimitFloor
    ) {
      toast({
        variant: 'error',
        title: `Role max limit cannot be reduced below ${roleMaxLimitFloor}`,
        description:
          'This journal is active or already has submissions, so the limit can only be increased.',
        isClosable: true
      });
      return;
    }

    if (form.roleMaxLimit !== undefined && form.roleMaxLimit > LIMIT_MAX) {
      toast({
        variant: 'error',
        title: `Role max limit must be ${LIMIT_MAX} or less`,
        isClosable: true
      });
      return;
    }

    if (
      !isOwner &&
      isCOCRecommendationRequired &&
      form.maxRecommendationRequiredCount === undefined
    ) {
      toast({
        variant: 'error',
        title: 'Max recommendations required is required',
        isClosable: true
      });
      return;
    }

    if (
      !isOwner &&
      isCOCRecommendationRequired &&
      form.maxRecommendationRequiredCount !== undefined &&
      form.maxRecommendationRequiredCount < 1
    ) {
      toast({
        variant: 'error',
        title: 'Max recommendations required must be at least 1',
        isClosable: true
      });
      return;
    }

    // Max recommendations required must not exceed the shared upper bound (other roles)
    if (
      !isOwner &&
      isCOCRecommendationRequired &&
      form.maxRecommendationRequiredCount !== undefined &&
      form.maxRecommendationRequiredCount > LIMIT_MAX
    ) {
      toast({
        variant: 'error',
        title: `Max recommendations required must be ${LIMIT_MAX} or less`,
        isClosable: true
      });
      return;
    }

    // Max recommendations required must not exceed role max limit (other roles)
    if (
      !isOwner &&
      isCOCRecommendationRequired &&
      form.maxRecommendationRequiredCount !== undefined &&
      form.roleMaxLimit !== undefined &&
      form.maxRecommendationRequiredCount > form.roleMaxLimit
    ) {
      toast({
        variant: 'error',
        title:
          'Max recommendations required must be less than or equal to role max limit',
        isClosable: true
      });
      return;
    }

    // Recommendation validation — mandatory for other roles when required
    if (!isOwner && isCOCRecommendationRequired && !form.decision?.length) {
      toast({
        variant: 'error',
        title: 'Recommendation is required',
        isClosable: true
      });
      return;
    }

    onSave({
      ...form,
      roleLevel: isOwner ? 1 : form.roleLevel,
      isOwner, // Always enforce based on type
      isDecisionMaker: isOwner ? true : form.isDecisionMaker ?? false // Default to false if undefined
    });
  };

  const handleClose = () => {
    setForm(seed);
    onClose();
  };

  const title =
    mode === 'add'
      ? isOwner
        ? 'Add Owner'
        : 'Add Other Role'
      : isOwner
      ? 'Edit Owner'
      : 'Edit Other Role';

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      width={{ base: 'calc(100vw - 16px)', md: '600px' } as any}
      closeButtonProps={{ hidden: true }}
    >
      <Flex flexDirection={'column'} gap={4} padding={{ base: 4, md: 8 }}>
        <Text textStyle="headingGroup">{title}</Text>

        {/* Role Level — hidden for owners, auto-assigned for other roles */}
        {!isOwner && (
          <Flex flexDirection="column" gap={1}>
            <Text textStyle="textContext">Role Level</Text>
            {mode === 'add' ? (
              <Text textStyle="textBody" fontWeight="semibold">
                {form.roleLevel}
              </Text>
            ) : (
              <Select
                value={form.roleLevel ?? ''}
                onChange={e => updateField('roleLevel', Number(e.target.value))}
                aria-label="Role Level"
              >
                <option value="">Select level</option>
                {(() => {
                  // Levels are based on number of other-role rows, not current values.
                  // For N rows, available levels are 2 through N+1.
                  // Also include the editing role's own level and any existing higher levels.
                  const rowCount = assignedRoleLevels.length;
                  const editingLevel = editingRole?.roleLevel ?? 0;
                  const maxLevel = Math.max(
                    rowCount + 1,
                    ...assignedRoleLevels,
                    editingLevel
                  );
                  return Array.from(
                    { length: maxLevel - 1 },
                    (_, i) => i + 2
                  ).map(level => (
                    <option key={level} value={level}>
                      {level}
                    </option>
                  ));
                })()}
              </Select>
            )}
          </Flex>
        )}

        {/* Role — dropdown */}
        <Flex flexDirection="column" gap={1}>
          <Text textStyle="textContext">Role</Text>
          <Select
            value={form.role}
            onChange={e => updateField('role', e.target.value)}
            aria-label="Role"
          >
            <option value="">Select role</option>
            {selectableRoles.map(role => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
          </Select>
        </Flex>

        {/* Role Abbreviation */}
        <Flex flexDirection="column" gap={1}>
          <Text textStyle="textContext">Role Abbreviation</Text>
          <Input
            name="roleAbbreviation"
            value={form.roleAbbreviation ?? ''}
            onChange={e => updateField('roleAbbreviation', e.target.value)}
            placeholder="e.g. Editor-In-Chief"
            disabled={true}
          />
        </Flex>

        {/* Role Max Limit */}
        <Flex flexDirection="column" gap={1}>
          <Flex alignItems="center" gap={1}>
            <Text textStyle="textContext">Role Max Limit</Text>
            <Text as="span" color="actionSecondaryCautionContent">
              *
            </Text>
          </Flex>
          <Input
            name="roleMaxLimit"
            type="number"
            min={roleMaxLimitFloor ?? 1}
            step={1}
            max={LIMIT_MAX}
            value={form.roleMaxLimit ?? ''}
            onChange={e => {
              const value = toOptionalInteger(e.target.value);
              if (value === 0) return;

              updateField('roleMaxLimit', value);
            }}
            placeholder="e.g. 1"
          />
          {roleMaxLimitFloor !== undefined && (
            <Text textStyle="textContext" color="gray.600">
              Can be increased but not reduced below the current limit of{' '}
              {roleMaxLimitFloor}, because this journal is active or already has
              submissions.
            </Text>
          )}
        </Flex>

        {/* Max Recommendations Required — other roles only when recommendations are required */}
        {!isOwner && isCOCRecommendationRequired && (
          <Flex flexDirection="column" gap={1}>
            <Flex alignItems="center" gap={1}>
              <Text textStyle="textContext">Max Recommendations Required</Text>
              <Text as="span" color="actionSecondaryCautionContent">
                *
              </Text>
            </Flex>
            <Input
              name="maxRecommendationRequiredCount"
              type="number"
              min={1}
              max={LIMIT_MAX}
              step={1}
              value={form.maxRecommendationRequiredCount ?? ''}
              onChange={e => {
                const value = toOptionalInteger(e.target.value);
                if (value === 0) return;

                updateField('maxRecommendationRequiredCount', value);
              }}
              placeholder="e.g. 2"
            />
            <Text textStyle="textContext" color="gray.600">
              Must be less than or equal to Role Max Limit.
            </Text>
          </Flex>
        )}

        {/* Decisions — shown only for other roles when recommendations are required */}
        {!isOwner && isCOCRecommendationRequired && (
          <Flex flexDirection="column" gap={1}>
            <Text textStyle="textContext">Recommendations</Text>
            <FlexibleMultiSelect
              options={DECISION_OPTIONS}
              value={decisionsToValues(form.decision || [])}
              onChange={values =>
                updateField('decision', valuesToDecisions(values))
              }
              placeholder="Select recommendations"
              showSelectAllOption
            />
          </Flex>
        )}

        {/* Submission Visibility */}
        <Flex
          flexDirection="column"
          gap={1}
          display={isOwner ? 'none' : 'block'}
        >
          <Text textStyle="textContext">Submission Visibility</Text>
          <Flex gap={4} alignItems="center" flexWrap="wrap">
            <Radio
              name="roleForm-onlyAssignedSubmissionVisibility"
              id="roleForm-onlyAssignedSubmissionVisibility-all"
              ariaLabel="All submissions"
              value="false"
              checked={form.onlyAssignedSubmissionVisibility !== true}
              onChange={() =>
                updateField('onlyAssignedSubmissionVisibility', false)
              }
            >
              All submissions
            </Radio>
            <Radio
              name="roleForm-onlyAssignedSubmissionVisibility"
              id="roleForm-onlyAssignedSubmissionVisibility-assigned"
              ariaLabel="Assigned submissions only"
              value="true"
              checked={form.onlyAssignedSubmissionVisibility === true}
              onChange={() =>
                updateField('onlyAssignedSubmissionVisibility', true)
              }
            >
              Assigned submissions only
            </Radio>
          </Flex>
        </Flex>
        {/* Auto Assign */}
        <Flex alignItems="center" gap={2}>
          <Checkbox
            id="roleForm-autoAssign"
            name="roleForm-autoAssign"
            ariaLabel="Auto Assign"
            isChecked={form.autoAssign}
            onChange={e => updateField('autoAssign', e.target.checked)}
          >
            Auto Assign
          </Checkbox>
        </Flex>

        {/* Is Owner */}
        <Flex alignItems="center" gap={2} display={isOwner ? 'block' : 'none'}>
          <Checkbox
            id="roleForm-isOwner"
            name="roleForm-isOwner"
            ariaLabel="Is Owner"
            isChecked={isOwner}
            disabled
          >
            Is Owner
          </Checkbox>
        </Flex>

        {/* Decision Maker */}
        <Flex alignItems="center" gap={2} display={'none'}>
          <Checkbox
            id="roleForm-isDecisionMaker"
            name="roleForm-isDecisionMaker"
            ariaLabel="Decision Maker"
            isChecked={form.isDecisionMaker ?? false}
            onChange={e => updateField('isDecisionMaker', e.target.checked)}
          >
            Decision Maker
          </Checkbox>
        </Flex>

        {/* Actions */}
        <Flex
          justifyContent="flex-end"
          gap={2}
          alignItems="center"
          marginTop={8}
        >
          <Button
            variant="secondary"
            onClick={handleClose}
            isDisabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            iconLeft={<FontAwesomeIcon icon={faFloppyDisk} />}
            onClick={handleSave}
            isLoading={isSaving}
          >
            Save
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};

export default RoleFormModal;
